import styled from "@emotion/styled";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  COLOR_GREY_2,
  COLOR_WHITE,
  COLOR_GREY_10,
  COLOR_PRIMARY_MAIN,
  COLOR_GREY_5,
  COLOR_GREY_1,
} from "shared/theme/colors";
import { flexCenter } from "shared/theme/mixins";
import FlexCenter from "../Layout/components/FlexCenter";
import { CellAlignment } from "./types";

export const StyledPaper = styled(Paper)<{ maxHeight?: string }>`
  width: 100%;
  border-radius: 0.75rem;
  overflow: auto;
  max-height: ${({ maxHeight }) => `calc(100vh - ${maxHeight ?? "196px"})`};
  min-height: 200px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

export const StyledTable = styled(Table)`
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;

  .MuiTableCell-root {
    height: 3rem;
    box-sizing: border-box;
    line-height: 1.43;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

export const StyledTableHead = styled(TableHead)`
  position: sticky;
  top: 0;
  z-index: 4;
  background-color: ${COLOR_GREY_1};
  width: 100%;
  height: 3rem;
  display: table-header-group;
  box-sizing: border-box;

  & th {
    height: 3rem;
    padding: 0 0.75rem;
    vertical-align: middle;
    box-sizing: border-box;

    :first-of-type {
      padding-left: 1.5rem;
    }

    :last-of-type {
      padding-right: 1rem;
    }
  }
`;

export const StyledHeaderRow = styled(TableRow)`
  width: 100%;
  display: table-row;
  height: 3rem;
  box-sizing: border-box;
`;

export const StyledHeaderCell = styled(TableCell)<{
  width?: string;
  align?: CellAlignment;
}>`
  padding: 0 0.75rem;
  border-bottom: 1px solid ${COLOR_GREY_5};
  text-align: ${({ align }) => align ?? "left"};
  background-color: ${COLOR_GREY_1};
  font-weight: bold;
  height: 3rem;
  vertical-align: middle;
  box-sizing: border-box;

  :first-of-type {
    padding-left: 1.5rem;
  }

  :last-of-type {
    padding-right: 1rem;
  }
`;

export const StyledRow = styled(TableRow)`
  display: table-row;
  width: 100%;
  background-color: ${COLOR_WHITE};
  height: 3rem;
  box-sizing: border-box;

  &:hover {
    background-color: ${COLOR_GREY_2};
  }
`;

export const StyledCell = styled(TableCell)<{
  width?: string;
  textTransform?: "capitalize" | "none";
  align?: CellAlignment;
}>`
  padding: 0 0.75rem;
  border-bottom: 1px solid ${COLOR_GREY_5};
  text-align: ${({ align }) => align ?? "left"};
  height: 3rem;
  vertical-align: middle;
  box-sizing: border-box;
  text-transform: ${({ textTransform }) => textTransform ?? "none"};
  width: ${({ width }) => width ?? "auto"};
  max-width: ${({ width }) => width ?? "auto"};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  :first-of-type {
    padding-left: 1.5rem;
  }

  :last-of-type {
    padding-right: 1rem;
  }
`;

export const StyledGroupRow = styled(TableRow)`
  display: table-row;
  width: 100%;
  height: 3rem;
  box-sizing: border-box;
`;

export const StyledGroupCell = styled(TableCell)`
  padding: 0;
  border: none;
  width: 100%;
`;

export const StyledAccordion = styled(Accordion)`
  box-shadow: none;
  width: 100%;

  &:before {
    display: none;
  }

  border-bottom: 1px solid ${COLOR_GREY_5};

  &:last-child {
    border-bottom: 0;
  }
`;

export const StyledAccordionSummary = styled(AccordionSummary)<{
  index?: number;
}>`
  background-color: ${COLOR_GREY_2};
  min-height: 3rem;
  height: 3rem;
  padding: 0 1.5rem;
  position: sticky;
  top: 3rem;
  z-index: 3;
  width: 100%;
  border-top: ${({ index }) =>
    index && index > 0 ? `1px solid ${COLOR_GREY_5}` : "none"};
  border-bottom: 1px solid ${COLOR_GREY_5};
  box-sizing: border-box;

  &.Mui-expanded {
    min-height: 3rem;
    height: 3rem;
  }

  & .MuiAccordionSummary-content {
    margin: 0;
    display: flex;
    align-items: center;
    height: 3rem;
    box-sizing: border-box;

    &.Mui-expanded {
      margin: 0;
    }
  }
`;

export const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 0;
  width: 100%;
`;

export const StyledCountBadge = styled(Chip)`
  background-color: ${COLOR_PRIMARY_MAIN};
  color: ${COLOR_WHITE};
  margin-left: 0.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
`;

export const StyledHeaderBox = styled(Box)`
  display: flex;
  align-items: center;
`;

export const StyledNoDataMessage = styled(Box)`
  padding: 1.5rem;
  text-align: center;
  color: ${COLOR_GREY_10};
  width: 100%;
`;

export const StyledEmptyGroupMessage = styled(Box)`
  padding: 0.75rem 1.5rem;
  text-align: left;
  color: ${COLOR_GREY_10};
  width: 100%;
`;

export const StyledSpinnerWrapper = styled(FlexCenter)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const StyledLoadingBox = styled(Box)`
  width: 100%;
  padding: 1.5rem 0;
  text-align: center;
`;

export const StyledNoDataBox = styled(Box)`
  width: 100%;
  padding: 1.5rem;
  text-align: center;
`;

export const StyledNestedTable = styled(Table)`
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
`;

export const StyledNestedTableBody = styled(TableBody)`
  width: 100%;
`;

export const StyledEmptyCell = styled(TableCell)`
  padding: 0.75rem 1.5rem;
  height: 3rem;
`;

export const StyledColGroup = styled.colgroup``;

export const StyledCol = styled.col<{ columnWidth?: string }>`
  width: ${({ columnWidth }) => columnWidth ?? "auto"};
`;

export const StyledNameCell = styled(Box)({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
  display: "block",
  maxWidth: "100%",
  cursor: "pointer",
});

export const StyledEmptyWrapper = styled(Box)`
  ${flexCenter()};
  height: 80px;
  border-radius: 1rem;
  background-color: ${COLOR_GREY_2};
`;
